/**
 * Versions anglaises du blog (8/10/2026).
 *
 * CONSTAT. `/en/blog/<slug>` répond pour TOUS les articles, mais seuls 3 des
 * 131 articles publiés ont une traduction (`titleEn` et `contentEn`). Les
 * autres affichent le texte français sous l'habillage anglais, en se
 * déclarant pourtant page canonique et version `en-US` (hreflang). Google
 * indexe donc deux URL au contenu identique, et c'est parfois la version
 * /en qu'il montre sur des requêtes françaises : `/en/blog/quest-ce-quun-puit-de-carbone`
 * capte « puits de carbone » (position 32), `/en/blog/quest-ce-que-net-zero-…`
 * « net zero definition » (35), à la place de leur page française.
 *
 * DÉCISION : canonique vers la page française, pas `noindex`.
 *   - La page /en non traduite est un DOUBLON de la page française (même
 *     texte, même langue) : c'est exactement le cas d'usage de
 *     `rel=canonical`. Google regroupe les deux URL et reporte les signaux
 *     (liens, impressions déjà acquises) sur la page française.
 *   - `noindex` ferait disparaître l'URL /en sans rien transmettre : on
 *     perdrait les positions déjà gagnées par ces doublons.
 *   - hreflang ne relie que des traductions : un article non traduit n'émet
 *     aucune alternative `en-US`, ni sur sa page française ni sur la page /en
 *     (une alternative qui pointe vers une page canonisée ailleurs est une
 *     erreur signalée par Search Console).
 *   - Le plan du site ne déclare la version /en que des articles traduits.
 * Un article traduit garde ses deux versions, chacune canonique d'elle-même,
 * reliées par hreflang (fr-FR, en-US, x-default → français).
 *
 * Module sans dépendance (importé par la page serveur, le composant client
 * et le plan du site) ; testé par scripts/tests/blog-traduction.test.ts.
 */

type Noeud = { text?: unknown; children?: unknown }

/** Vrai si un contenu riche Lexical contient au moins un texte non vide. */
export function contenuRedige(contenu: unknown): boolean {
  if (!contenu || typeof contenu !== 'object') return false
  const pile: unknown[] = [(contenu as { root?: unknown }).root ?? contenu]
  while (pile.length) {
    const n = pile.pop() as Noeud | null
    if (!n || typeof n !== 'object') continue
    if (typeof n.text === 'string' && n.text.trim()) return true
    if (Array.isArray(n.children)) pile.push(...n.children)
  }
  return false
}

export interface ArticleBilingue {
  slug: string
  titleEn?: string | null
  contentEn?: unknown
}

/** Un article est traduit s'il a un titre ET un texte anglais. Un titre seul ne suffit pas. */
export function estTraduit(a: ArticleBilingue): boolean {
  return typeof a.titleEn === 'string' && a.titleEn.trim().length > 0 && contenuRedige(a.contentEn)
}

export const cheminArticle = (slug: string, locale: 'fr' | 'en' = 'fr'): string =>
  locale === 'en' ? `/en/blog/${slug}` : `/blog/${slug}`

export interface BalisesLangue {
  /** Chemin canonique (relatif, résolu par `metadataBase`). */
  canonical: string
  /** Alternatives hreflang, seulement pour un article traduit. */
  languages?: Record<string, string>
  /** Langue réelle du contenu affiché (Open Graph). */
  ogLocale: 'fr_FR' | 'en_US'
  /** Vrai si la page affiche réellement une version anglaise. */
  versionAnglaise: boolean
}

/**
 * Canonique et hreflang d'un article, selon la langue demandée et l'existence
 * d'une traduction. `canonicalUrl` (champ SEO saisi à la main, article publié
 * ailleurs) l'emporte toujours.
 */
export function balisesLangue(
  a: ArticleBilingue & { seo?: { canonicalUrl?: string | null } | null },
  locale: 'fr' | 'en',
  traduit = estTraduit(a),
): BalisesLangue {
  const fr = cheminArticle(a.slug, 'fr')
  const en = cheminArticle(a.slug, 'en')
  const versionAnglaise = locale === 'en' && traduit
  return {
    canonical: a.seo?.canonicalUrl || (versionAnglaise ? en : fr),
    ...(traduit ? { languages: { 'fr-FR': fr, 'en-US': en, 'x-default': fr } } : {}),
    ogLocale: versionAnglaise ? 'en_US' : 'fr_FR',
    versionAnglaise,
  }
}
